<script lang="ts">
  /**
   * AnalysisProgress — exibe o progresso do worker de análise.
   *
   * Recebe WorkerClientState e mostra:
   * - Stepper interno com etapas amigáveis mapeadas a partir de WorkerStep
   * - Barra de progresso percentual
   * - Botão "Cancelar análise" (dispatch event 'cancel')
   * - Painel de erro se state.error estiver preenchido
   */
  import { createEventDispatcher } from 'svelte';
  import Stepper from '../../components/Stepper.svelte';
  import type { WorkerClientState } from '../../workers/workerClient';
  import type { WorkerStep } from '../../workers/workerMessages';

  export let state: WorkerClientState | null = null;

  const dispatch = createEventDispatcher<{ cancel: void }>();

  const WORKER_STEPS: Array<{ id: WorkerStep; label: string }> = [
    { id: 'reading', label: 'Lendo arquivos' },
    { id: 'parsing-afd', label: 'Parse AFD' },
    { id: 'validating', label: 'Validando' },
    { id: 'normalizing', label: 'Normalizando' },
    { id: 'metrics', label: 'Métricas' },
    { id: 'report', label: 'Relatório' },
  ];

  $: currentStep = state?.step ?? 'idle';
  $: percent = state?.percent ?? 0;
  $: label = state?.label ?? 'Aguardando...';
  $: hasError = !!(state?.error);

  function handleCancel() {
    dispatch('cancel');
  }
</script>

<div class="analysis-progress" data-testid="analysis-progress">
  {#if hasError}
    <div class="analysis-progress__error" role="alert">
      <h3 class="analysis-progress__error-title">Erro na análise</h3>
      <p class="analysis-progress__error-msg">{state?.error}</p>
      <button class="analysis-progress__cancel-btn" type="button" on:click={handleCancel}>
        Voltar
      </button>
    </div>
  {:else}
    <div class="analysis-progress__stepper">
      <Stepper steps={WORKER_STEPS} currentStep={currentStep} />
    </div>

    <div class="analysis-progress__bar-wrap" aria-label="Progresso da análise">
      <div
        class="analysis-progress__bar"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div class="analysis-progress__fill" style="width: {percent}%"></div>
      </div>
      <span class="analysis-progress__percent">{percent}%</span>
    </div>

    <p class="analysis-progress__label" aria-live="polite">{label}</p>

    <div class="analysis-progress__actions">
      <button class="analysis-progress__cancel-btn" type="button" on:click={handleCancel}>
        Cancelar análise
      </button>
    </div>
  {/if}
</div>

<style>
  .analysis-progress {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-5, 1.25rem);
    max-width: 720px;
    margin: 0 auto;
    padding: var(--ptt-space-6, 1.5rem) 0;
  }

  .analysis-progress__bar-wrap {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-3, 12px);
  }

  .analysis-progress__bar {
    flex: 1;
    height: 12px;
    background: var(--ptt-gray-20, #e7e7e7);
    border-radius: var(--ptt-radius-sm, 4px);
    overflow: hidden;
  }

  .analysis-progress__fill {
    height: 100%;
    background: var(--ptt-purple-100, #5B2FD4);
    border-radius: var(--ptt-radius-sm, 4px);
    transition: width 0.3s ease;
  }

  .analysis-progress__percent {
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--ptt-gray-300, #323232);
    min-width: 3rem;
    text-align: right;
  }

  .analysis-progress__label {
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.9375rem;
    color: var(--ptt-gray-200, #565656);
    margin: 0;
  }

  .analysis-progress__actions {
    display: flex;
    justify-content: flex-end;
  }

  .analysis-progress__cancel-btn {
    background: transparent;
    border: 1px solid var(--ptt-gray-50, #c0c0c0);
    border-radius: var(--ptt-radius-sm, 4px);
    padding: var(--ptt-space-2, 8px) var(--ptt-space-5, 24px);
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-gray-200, #565656);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  .analysis-progress__cancel-btn:hover {
    background: var(--ptt-gray-10, #f5f5f5);
    border-color: var(--ptt-gray-100, #7d7d7d);
  }

  .analysis-progress__error {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3, 12px);
    padding: var(--ptt-space-5, 1.25rem);
    background: var(--ptt-yellow-10, #FEF3CF);
    border-left: 4px solid var(--ptt-orange-100, #FB7224);
    border-radius: var(--ptt-radius-md, 8px);
  }

  .analysis-progress__error-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--ptt-gray-300, #323232);
  }

  .analysis-progress__error-msg {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--ptt-gray-200, #565656);
  }
</style>
